import React, { useRef, useMemo, useState, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useTexture, Billboard, Html } from '@react-three/drei'
import * as THREE from 'three'

// --- Constants ---
const HAMMER_SCALE = 32
const THROW_DURATION = 1.6
const RETURN_DURATION = 0.8
const PAUSE_DURATION = 0.4
const ARC_HEIGHT = 8
const SHEET_COLS = 9
const SHEET_PATH = '/assets/effects/hammer_slam/hammer_slam_sheet.png'
const TOTAL_FRAMES = 81
const FPS = 60

// Config from ShockWaveconfig.json - enhanced for visibility
const SHOCKWAVE_CONFIG = {
    "waveSpeed": 0.7,
    "intensity": 1.8,
    "color": "#f4c725",
    "debrisCount": 400, // Doubled from 200
    "decay": 2, // Controls lifetime
    "autoFire": false
}

useTexture.preload(SHEET_PATH)

// --- Helpers ---
const useScreenTo3D = (screenX: number, screenY: number) => {
    const { camera, size } = useThree()
    return useMemo(() => {
        const ndcX = (screenX / size.width) * 2 - 1
        const ndcY = -(screenY / size.height) * 2 + 1
        const raycaster = new THREE.Raycaster()
        raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera)
        const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)
        const target = new THREE.Vector3()
        raycaster.ray.intersectPlane(groundPlane, target)
        return target || new THREE.Vector3(0, 0, 0)
    }, [screenX, screenY, size, camera])
}

const range = (min: number, max: number) => Math.random() * (max - min) + min

// --- 2D Canvas Shockwave (Direct port from VolumetricShockwave.tsx reference) ---
// This renders a 2D canvas overlay at the impact screen position
const VolumetricShockwave2D = ({ screenX, screenY, triggerTime }: { screenX: number; screenY: number; triggerTime: number }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const animationRef = useRef<number>(0)
    const lastTriggerRef = useRef(0)

    useEffect(() => {
        if (triggerTime === 0 || triggerTime === lastTriggerRef.current) return
        lastTriggerRef.current = triggerTime

        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        const size = 900
        canvas.width = size
        canvas.height = size

        let lastFrameTs = 0

        const config = SHOCKWAVE_CONFIG
        const hexToRgb = (hex: string) => {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
            return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : { r: 255, g: 255, b: 255 }
        }
        const rgb = hexToRgb(config.color)

        // Center of canvas = impact point
        const cx = size / 2
        const cy = size / 2

        // Particle class - exact copy from reference
        class Particle {
            x: number
            y: number
            vx: number
            vy: number
            life: number
            decay: number
            size: number

            constructor(x: number, y: number, s: number, ang?: number) {
                this.x = x
                this.y = y
                const a = ang ?? Math.random() * 6.28
                const v = s * (0.5 + Math.random())
                this.vx = Math.cos(a) * v
                this.vy = Math.sin(a) * v
                this.life = 1
                this.decay = 0.005 + Math.random() * 0.02
                this.size = 2 + Math.random() * 4
            }

            update(df: number = 1) {
                this.x += this.vx * df
                this.y += this.vy * df
                this.vx *= Math.pow(0.96, df)
                this.vy *= Math.pow(0.96, df)
                this.life -= this.decay * df
            }

            draw(ctx: CanvasRenderingContext2D) {
                if (this.life <= 0) return
                const alpha = this.life * 0.6
                const g = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.size)
                g.addColorStop(0, `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`)
                g.addColorStop(1, `rgba(${rgb.r},${rgb.g},${rgb.b},0)`)
                ctx.fillStyle = g
                ctx.beginPath()
                ctx.arc(this.x, this.y, this.size, 0, 6.28)
                ctx.fill()
            }
        }

        // Blast class - exact copy from reference
        class Blast {
            x: number
            y: number
            age: number
            maxAge: number
            r: number
            maxR: number

            constructor(x: number, y: number) {
                this.x = x
                this.y = y
                this.age = 0
                this.maxAge = 120 / config.decay
                this.r = 0
                this.maxR = size * 0.4 // Fit within canvas
            }

            update(parts: Particle[], df: number = 1) {
                const prog = this.age / this.maxAge
                const ease = 1 - Math.pow(1 - prog, 3)
                const prevR = this.r
                this.r = ease * this.maxR * (config.waveSpeed * 0.5)
                this.age += df

                // Spawn wave-riding particles
                if (this.r - prevR > 0.5 && prog < 0.8) {
                    const count = Math.ceil(2 * Math.PI * this.r / 100 * config.debrisCount * 0.05)
                    for (let i = 0; i < count; i++) {
                        if (Math.random() > 0.3) continue
                        const a = Math.random() * 6.28
                        const p = new Particle(this.x + Math.cos(a) * this.r, this.y + Math.sin(a) * this.r, 1, a)
                        p.vx += Math.cos(a) * (this.r - prevR)
                        p.vy += Math.sin(a) * (this.r - prevR)
                        p.size *= 0.8
                        parts.push(p)
                    }
                }
            }

            draw(ctx: CanvasRenderingContext2D) {
                const prog = this.age / this.maxAge
                const op = 1 - prog
                if (op <= 0) return

                const width = 60 * config.intensity * (1 - prog * 0.5)
                const layers = 15

                for (let i = 0; i < layers; i++) {
                    const r = i / (layers - 1)
                    const off = (r - 0.5) * width
                    const dist = Math.abs(r - 0.5) * 2
                    const lop = (1 - Math.pow(dist, 2)) * op * 0.15 * config.intensity
                    if (lop < 0.005) continue
                    ctx.beginPath()
                    ctx.arc(this.x, this.y, Math.max(0, this.r + off), 0, 6.28)
                    ctx.strokeStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${lop})`
                    ctx.lineWidth = (width / layers) * 1.5
                    ctx.stroke()
                }
            }
        }

        const blasts: Blast[] = []
        const parts: Particle[] = []

        // Fire initial blast at center
        blasts.push(new Blast(cx, cy))
        for (let i = 0; i < config.debrisCount; i++) {
            parts.push(new Particle(cx, cy, config.waveSpeed * 10))
        }

        const loop = (ts: number) => {
            const dtMs = lastFrameTs === 0 ? 0 : Math.min(ts - lastFrameTs, 50)
            lastFrameTs = ts
            const df = dtMs === 0 ? 1 : dtMs / 16.67

            ctx.clearRect(0, 0, size, size)
            ctx.globalCompositeOperation = 'lighter'

            for (let i = blasts.length - 1; i >= 0; i--) {
                blasts[i].update(parts, df)
                blasts[i].draw(ctx)
                if (blasts[i].age >= blasts[i].maxAge) blasts.splice(i, 1)
            }

            for (let i = parts.length - 1; i >= 0; i--) {
                parts[i].update(df)
                parts[i].draw(ctx)
                if (parts[i].life <= 0) parts.splice(i, 1)
            }

            if (blasts.length > 0 || parts.length > 0) {
                animationRef.current = requestAnimationFrame(loop)
            }
        }

        animationRef.current = requestAnimationFrame(loop)

        return () => {
            cancelAnimationFrame(animationRef.current)
        }
    }, [triggerTime])

    // Canvas is centered by Html component - use relative positioning 
    const canvasSize = 900 // Increased from 600 for larger effect
    const style: React.CSSProperties = {
        width: canvasSize,
        height: canvasSize,
        marginLeft: -canvasSize / 2,
        marginTop: -canvasSize / 2,
        pointerEvents: 'none',
    }

    return <canvas ref={canvasRef} style={style} />
}


// --- 2D Animated Sprite Hammer ---
const HammerSprite = ({ sourcePos, targetPos, onImpact, onComplete, isTargetPerspective }: {
    sourcePos: THREE.Vector3,
    targetPos: THREE.Vector3,
    onImpact: () => void,
    onComplete: () => void,
    isTargetPerspective: boolean
}) => {
    const group = useRef<THREE.Group>(null)
    const shadowRef = useRef<THREE.Mesh>(null)
    const active = useRef(true)
    const isReady = useRef(false)

    const textRef = useTexture(SHEET_PATH)
    const texture = useMemo(() => {
        const t = textRef.clone()
        t.repeat.set(1 / SHEET_COLS, 1 / SHEET_COLS)
        t.magFilter = THREE.LinearFilter
        t.minFilter = THREE.LinearFilter
        t.colorSpace = THREE.SRGBColorSpace
        return t
    }, [textRef])

    const shadowTexture = useMemo(() => {
        const t = textRef.clone()
        t.repeat.set(1 / SHEET_COLS, 1 / SHEET_COLS)
        return t
    }, [textRef])

    const impactTriggered = useRef(false)
    const [phase, setPhase] = useState<'THROW' | 'PAUSE' | 'RETURN' | 'DONE'>('THROW')

    // Accumulator for custom timeline
    const timeRef = useRef(0)

    useFrame((state, delta) => {
        if (!group.current) return

        const dt = Math.min(delta, 0.1)
        timeRef.current += dt

        const totalElapsed = timeRef.current

        // 1. Calculate Position & Phase
        let currentPos = sourcePos.clone()

        if (phase === 'THROW') {
            const progress = Math.min(totalElapsed / THROW_DURATION, 1)

            currentPos.lerpVectors(sourcePos, targetPos, progress)
            currentPos.y += Math.sin(progress * Math.PI) * ARC_HEIGHT

            if (progress >= 1) {
                currentPos.copy(targetPos)
                if (!impactTriggered.current) {
                    impactTriggered.current = true
                    onImpact()
                }
                setPhase('PAUSE')
            }
        } else if (phase === 'PAUSE') {
            currentPos.copy(targetPos)
            if (totalElapsed > THROW_DURATION + PAUSE_DURATION) {
                setPhase('RETURN')
            }
        } else if (phase === 'RETURN') {
            const returnElapsed = totalElapsed - (THROW_DURATION + PAUSE_DURATION)
            const progress = Math.min(returnElapsed / RETURN_DURATION, 1)
            const ease = progress * progress * (3 - 2 * progress)

            currentPos.lerpVectors(targetPos, sourcePos, ease)
            currentPos.y += Math.sin(ease * Math.PI) * (ARC_HEIGHT * 0.5)

            if (progress >= 1) {
                setPhase('DONE')
                active.current = false
                onComplete()
            }
        }

        isReady.current = true
        group.current.position.copy(currentPos)

        // Update Shadow
        if (shadowRef.current) {
            shadowRef.current.position.set(currentPos.x, 0.05, currentPos.z)
            const heightFactor = Math.max(0.5, 1 - (currentPos.y / ARC_HEIGHT) * 0.5)
            const s = HAMMER_SCALE * heightFactor
            if (isTargetPerspective) {
                shadowRef.current.scale.set(-s, s, 1)
            } else {
                shadowRef.current.scale.set(s, s, 1)
            }
            ; (shadowRef.current.material as THREE.MeshBasicMaterial).opacity = 0.4 * heightFactor
        }

        // 2. Animate Sprite Frame 
        const frameIndex = Math.floor(totalElapsed * FPS) % TOTAL_FRAMES

        const col = frameIndex % SHEET_COLS
        const row = Math.floor(frameIndex / SHEET_COLS)

        texture.offset.x = col / SHEET_COLS
        texture.offset.y = 1 - (row + 1) / SHEET_COLS

        if (shadowTexture) {
            shadowTexture.offset.x = texture.offset.x
            shadowTexture.offset.y = texture.offset.y
        }

        // 3. Orientation
        if (isTargetPerspective) {
            group.current.scale.set(-HAMMER_SCALE, HAMMER_SCALE, HAMMER_SCALE)
        } else {
            group.current.scale.set(HAMMER_SCALE, HAMMER_SCALE, HAMMER_SCALE)
        }
    })

    return (
        <>
            <group ref={group} position={sourcePos} visible={isReady.current}>
                <Billboard>
                    <mesh>
                        <planeGeometry args={[1, 1]} />
                        <meshBasicMaterial
                            map={texture}
                            transparent
                            opacity={1}
                            side={THREE.DoubleSide}
                            depthWrite={false}
                        />
                    </mesh>
                </Billboard>
            </group>

            {/* Existing particle trail logic (retained) */}
            <TrailParticles targetRef={group} active={active.current && phase === 'THROW'} />

            <mesh
                ref={shadowRef}
                position={[sourcePos.x, 0.05, sourcePos.z]}
                rotation={[-Math.PI / 2, 0, 0]}
                scale={[HAMMER_SCALE, HAMMER_SCALE, 1]}
                visible={isReady.current}
            >
                <planeGeometry args={[1, 1]} />
                <meshBasicMaterial
                    map={shadowTexture}
                    transparent
                    opacity={0.3}
                    color="black"
                    depthWrite={false}
                    depthTest={false}
                    side={THREE.DoubleSide}
                />
            </mesh>
        </>
    )
}

// Trail Particles (Copied from previous step for completeness)
const TrailParticles = ({ targetRef, active }: { targetRef: React.RefObject<THREE.Group | THREE.Mesh>, active: boolean }) => {
    const meshRef = useRef<THREE.InstancedMesh>(null)
    const count = 100
    const dummy = useMemo(() => new THREE.Object3D(), [])
    const particles = useRef(new Array(count).fill(0).map(() => ({
        life: 0,
        maxLife: 0,
        position: new THREE.Vector3(),
        velocity: new THREE.Vector3(),
        scale: 1,
        active: false,
        rotationSpeed: new THREE.Vector3(range(-5, 5), range(-5, 5), range(-5, 5))
    })))
    const currentIdx = useRef(0)
    const spawnTimer = useRef(0)

    useFrame((state, delta) => {
        if (!meshRef.current) return
        const dt = Math.min(delta, 0.1)
        if (active && targetRef.current) {
            spawnTimer.current += dt
            if (spawnTimer.current > 0.02) {
                spawnTimer.current = 0
                const p = particles.current[currentIdx.current]
                p.active = true
                p.life = 1.2
                p.maxLife = 1.2
                p.position.copy(targetRef.current.position)
                p.position.x += range(-0.2, 0.2)
                p.position.y += range(-0.2, 0.2)
                p.position.z += range(-0.2, 0.2)
                p.velocity.set(range(-0.3, 0.3), range(-0.3, 0.3), range(-0.3, 0.3))
                p.scale = range(0.4, 0.7)
                currentIdx.current = (currentIdx.current + 1) % count
            }
        }
        particles.current.forEach((p, i) => {
            if (p.active) {
                p.life -= dt
                if (p.life <= 0) p.active = false
                p.position.addScaledVector(p.velocity, dt)
                dummy.position.copy(p.position)
                const s = p.scale * (p.life / p.maxLife)
                dummy.scale.setScalar(s)
                dummy.rotation.x += p.rotationSpeed.x * dt
                dummy.rotation.y += p.rotationSpeed.y * dt
                dummy.updateMatrix()
                meshRef.current!.setMatrixAt(i, dummy.matrix)
            } else {
                dummy.scale.setScalar(0)
                dummy.updateMatrix()
                meshRef.current!.setMatrixAt(i, dummy.matrix)
            }
        })
        meshRef.current.instanceMatrix.needsUpdate = true
    })

    return (
        <instancedMesh ref={meshRef} args={[undefined, undefined, count]} renderOrder={90}>
            <dodecahedronGeometry args={[0.4, 0]} />
            <meshBasicMaterial
                color="#fbbf24"
                transparent
                opacity={0.6}
                blending={THREE.AdditiveBlending}
                depthWrite={false}
            />
        </instancedMesh>
    )
}

// Flash Effect (Retained)
const FlashEffect = ({ position, triggerTime }: { position: THREE.Vector3; triggerTime: number }) => {
    const sphereRef = useRef<THREE.Mesh>(null)

    useFrame((state) => {
        if (!sphereRef.current) return
        if (triggerTime === 0) {
            sphereRef.current.visible = false
            return
        }
        const elapsed = state.clock.elapsedTime - triggerTime
        if (elapsed >= 0 && elapsed < 0.3) {
            sphereRef.current.visible = true
            const scale = 1 + elapsed * 20
            sphereRef.current.scale.setScalar(scale)
                ; (sphereRef.current.material as THREE.MeshBasicMaterial).opacity = 1 - (elapsed / 0.3)
        } else {
            sphereRef.current.visible = false
        }
    })

    return (
        <group position={[position.x, 2, position.z]}>
            <mesh ref={sphereRef} visible={false} renderOrder={101}>
                <sphereGeometry args={[2, 32, 32]} />
                <meshBasicMaterial
                    color="#fff7ed"
                    transparent
                    opacity={1}
                    depthWrite={false}
                    depthTest={false}
                    blending={THREE.AdditiveBlending}
                />
            </mesh>
        </group>
    )
}


// --- Main Export ---
export default function HammerSlam2D({ sourceX, sourceY, targetX, targetY, onComplete, isTargetPerspective = false }: {
    sourceX: number, sourceY: number, targetX: number, targetY: number, onComplete: () => void, isTargetPerspective?: boolean
}) {
    const startPos = useScreenTo3D(sourceX, sourceY)
    const endPos = useScreenTo3D(targetX, targetY)
    const [impactTime, setImpactTime] = useState(0)
    const { clock } = useThree()

    const handleImpact = () => {
        setImpactTime(clock.elapsedTime)
    }

    const adjustedSource = useMemo(() => {
        const p = startPos.clone()
        p.y += 2
        p.z += 2
        return p
    }, [startPos])

    const adjustedTarget = useMemo(() => {
        if (!isTargetPerspective) return endPos
        const p = endPos.clone()
        p.z += -3
        try { p.y = 0 } catch (e) { }
        return p
    }, [endPos, isTargetPerspective])

    return (
        <>
            <HammerSprite
                sourcePos={adjustedSource}
                targetPos={adjustedTarget}
                onImpact={handleImpact}
                onComplete={onComplete}
                isTargetPerspective={isTargetPerspective}
            />
            {/* 2D Canvas Shockwave - rendered via Html from drei */}
            <Html
                position={[adjustedTarget.x, 0.1, adjustedTarget.z]}
                zIndexRange={[0, 1]}
                style={{ pointerEvents: 'none' }}
            >
                <VolumetricShockwave2D screenX={0} screenY={0} triggerTime={impactTime} />
            </Html>
            <FlashEffect position={adjustedTarget} triggerTime={impactTime} />
        </>
    )
}
